import { useState } from "react";
import { BookOpen, CalendarRange, ChevronRight, Download, Layers3, ShieldCheck } from "lucide-react";
import { PasswordChangeForm } from "./AccountViews";
import { BackupView } from "./BackupView";
import type { AuthUser } from "./AuthApp";
import type { SchoolLevel } from "./catalog";
import "./navigation-hubs.css";

type Period = 1 | 2 | 3;

export function SettingsHub({
  teacherLevels,
  allLevels,
  activePeriod,
  onToggleLevel,
  onPeriodChange,
  showPrograms,
  onOpenPrograms,
}: {
  teacherLevels: SchoolLevel[];
  allLevels: SchoolLevel[];
  activePeriod: Period;
  onToggleLevel: (level: SchoolLevel) => void;
  onPeriodChange: (period: Period) => void;
  showPrograms: boolean;
  onOpenPrograms: () => void;
}) {
  return (
    <main className="management-page settings-hub-page">
      <div className="management-heading">
        <div><span className="eyebrow">Configuration pédagogique</span><h1>Paramètres</h1><p>Retrouve ici les réglages qui concernent ton enseignement.</p></div>
      </div>

      <section className="settings-hub-table">
        {showPrograms && <article className="settings-hub-row clickable" onClick={onOpenPrograms}>
          <div className="settings-hub-icon"><BookOpen size={22} /></div>
          <div className="settings-hub-copy"><strong>Programmes / Thèmes</strong><span>Gérer les programmes et l’organisation des thèmes par niveau.</span></div>
          <button type="button" onClick={(event) => { event.stopPropagation(); onOpenPrograms(); }}>Ouvrir <ChevronRight size={17} /></button>
        </article>}

        <article className="settings-hub-row">
          <div className="settings-hub-icon"><Layers3 size={22} /></div>
          <div className="settings-hub-copy"><strong>Niveaux</strong><span>Choisir les niveaux que tu enseignes et que CompetenceO doit afficher.</span></div>
          <div className="settings-level-pills">
            {allLevels.map((level) => {
              const checked = teacherLevels.includes(level);
              const locked = checked && teacherLevels.length === 1;
              return (
                <label key={level} className={checked ? "selected" : ""}>
                  <input type="checkbox" checked={checked} disabled={locked} onChange={() => onToggleLevel(level)} />
                  <span>{level}</span>
                </label>
              );
            })}
          </div>
        </article>

        <article className="settings-hub-row">
          <div className="settings-hub-icon"><CalendarRange size={22} /></div>
          <div className="settings-hub-copy"><strong>Période</strong><span>Définir la période active par défaut. Elle reste aussi modifiable depuis la séance.</span></div>
          <div className="settings-period-buttons" role="group" aria-label="Période active">
            {([1, 2, 3] as Period[]).map((period) => (
              <button type="button" key={period} className={activePeriod === period ? "active" : ""} onClick={() => onPeriodChange(period)}>P{period}</button>
            ))}
          </div>
        </article>
      </section>
    </main>
  );
}

export function AccountHub({
  currentUser,
  backupDisabled,
  onExport,
  onRestore, onLogout,
}: {
  onLogout?: () => void;
  currentUser: AuthUser;
  backupDisabled: boolean;
  onExport: () => Record<string, string>;
  onRestore: (data: Record<string, string>) => Promise<void>;
}) {
  const [tab, setTab] = useState<"account" | "backup">("account");

  return (
    <div className="management-page account-hub-page">
      <div className="management-heading">
        <div><span className="eyebrow">Espace personnel</span><h1>Mon compte</h1><p>{currentUser.fullName}{currentUser.email ? ` · ${currentUser.email}` : ""}</p></div>
      </div>

      <div className="account-hub-tabs" role="tablist" aria-label="Mon compte">
        <button type="button" className={tab === "account" ? "active" : ""} onClick={() => setTab("account")}><ShieldCheck size={17} /> Mon compte</button>
        <button type="button" className={tab === "backup" ? "active" : ""} onClick={() => setTab("backup")}><Download size={17} /> Sauvegarde</button>
      </div>

      {onLogout && <button onClick={onLogout}>Déconnexion</button>}
      {tab === "account" ? (
        <section className="account-hub-content"><PasswordChangeForm compact /></section>
      ) : (
        <section className="account-hub-content backup-tab-content"><BackupView currentUser={currentUser} disabled={backupDisabled} onExport={onExport} onRestore={onRestore} /></section>
      )}
    </div>
  );
}
